import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { countries } from "../components/CountryName";
import {
    GraduationCap,
    MapPin,
    Calendar,
    Trash2,
    AlertCircle,
    School,
    Globe,
    Plus,
    ChevronDown,
    CheckCircle,
    Edit2
} from "lucide-react";

// Create fresh copy of empty education
const createFreshEducation = (getEmptyEducation) => {
    const newEducation = getEmptyEducation();
    return { ...newEducation };
};

const EducationForm = ({ education, setEducation, getEmptyEducation, preview, handleChange, loading, errors, onSave }) => {
    const [expandedCards, setExpandedCards] = useState({});
    const [localErrors, setLocalErrors] = useState({});
    const [isEditing, setIsEditing] = useState(false);


    // Ensure education is always an array
    const safeEducation = Array.isArray(education) ? education : [];

    // Initialize expanded state for education cards
    useEffect(() => {
        const initialExpandedState = {};
        safeEducation.forEach((_, index) => {
            if (window.innerWidth < 768) {
                initialExpandedState[index] = index === 0;
            } else {
                initialExpandedState[index] = true;
            }
        });
        setExpandedCards(initialExpandedState);
    }, [safeEducation.length]);

    const toggleCard = (index) => {
        setExpandedCards(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const addEducation = useCallback(() => {
        if (setEducation && getEmptyEducation) {
            setEducation(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newEducation = createFreshEducation(getEmptyEducation);
                const newArray = [...currentArray, newEducation];
                const newIndex = newArray.length - 1;

                setExpandedCards(prevExpanded => ({
                    ...prevExpanded,
                    [newIndex]: true
                }));

                return newArray;
            });
        }
    }, [setEducation, getEmptyEducation]);

    const removeEducation = useCallback((index) => {
        if (setEducation) {
            setEducation(prev => {
                const currentArray = Array.isArray(prev) ? prev : [];
                const newArray = currentArray.filter((_, i) => i !== index);

                setExpandedCards(prevExpanded => {
                    const newExpanded = {};
                    Object.keys(prevExpanded).forEach(key => {
                        const numKey = parseInt(key);
                        if (numKey < index) {
                            newExpanded[numKey] = prevExpanded[numKey];
                        } else if (numKey > index) {
                            newExpanded[numKey - 1] = prevExpanded[numKey];
                        }
                    });
                    return newExpanded;
                });

                return newArray;
            });
        }
    }, [setEducation]);

    const handleChangeField = useCallback((index, field, value) => {
        if (handleChange) {
            handleChange(index, field, value);
        }
    }, [handleChange]);

    const formatDate = (date) => {
        if (!date) return "—";
        return new Date(date).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const validateForm = () => {
        const newErrors = {};
        safeEducation.forEach((edu, i) => {
            if (!edu.level) newErrors[`level_${i}`] = "Degree/Level is required";
            if (!edu.schoolName) newErrors[`schoolName_${i}`] = "School/University name is required";
            if (!edu.city) newErrors[`city_${i}`] = "City is required";
            if (!edu.country) newErrors[`country_${i}`] = "Country is required";
            if (!edu.from) newErrors[`from_${i}`] = "Start date is required";
            if (!edu.to) newErrors[`to_${i}`] = "End date is required";
            if (edu.from && edu.to && edu.from > edu.to) {
                newErrors[`to_${i}`] = "End date must be after start date";
            }
        });
        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview && !isEditing) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <GraduationCap size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Academic History</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Official Education Declaration</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Education history verified and approved</p>
                        </div>
                    )}

                    <div className="space-y-4 sm:space-y-5">
                        {safeEducation.filter(edu => edu.level).length === 0 ? (
                            <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                                <GraduationCap size={40} className="text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No education records added</p>
                            </div>
                        ) : (
                            safeEducation.filter(edu => edu.level).map((edu, i) => (
                                <div key={i} className="bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-5 hover:shadow-md transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {i + 1}
                                        </div>
                                        <h3 className="font-bold text-gray-800 text-sm sm:text-base">{edu.schoolName}</h3>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                        <Preview label="Degree/Level" value={edu.level} />
                                        <Preview label="School/University" value={edu.schoolName} />
                                        <Preview label="City" value={edu.city} />
                                        <Preview label="Country" value={edu.country} />
                                        <Preview label="Start Date" value={formatDate(edu.from)} />
                                        <Preview label="End Date" value={formatDate(edu.to)} />
                                        <div className="sm:col-span-2 lg:col-span-3">
                                            <Preview label="Address" value={edu.address} />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {(preview?.status === "reject" || !preview?.status) && (
                        <div className="flex justify-center pt-6 mt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-xl font-semibold text-sm transition-all shadow-md"
                            >
                                <Edit2 size={16} /> Edit Education Information
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Edit Mode
    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Saving education records...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <GraduationCap size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Academic History</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Add your educational qualifications</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {(Object.keys(localErrors).length > 0 || errors?.length > 0) && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                            {errors?.map((err, i) => (
                                <li key={`err-${i}`} className="text-rose-600 text-[11px] py-0.5">• {err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                    {safeEducation.length === 0 ? (
                        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <GraduationCap size={40} className="text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500 text-sm">No education records added</p>
                            <button
                                type="button"
                                onClick={addEducation}
                                className="mt-3 text-blue-600 text-sm font-medium flex items-center gap-1 mx-auto"
                            >
                                <Plus size={16} /> Add your first education record
                            </button>
                        </div>
                    ) : (
                        safeEducation.map((edu, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                                {/* Card Header - Clickable on mobile */}
                                <div
                                    className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                                    onClick={() => toggleCard(index)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                                            {index + 1}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                                                {edu.schoolName || "New Education Record"}
                                            </h3>
                                            {edu.level && (
                                                <p className="text-[10px] sm:text-xs text-gray-500">
                                                    {edu.level}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            className="md:hidden text-gray-400"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleCard(index);
                                            }}
                                        >
                                            <ChevronDown size={18} className={`transition-transform ${expandedCards[index] ? 'rotate-180' : ''}`} />
                                        </button>
                                        {safeEducation.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeEducation(index);
                                                }}
                                                className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className={`
                                    transition-all duration-300 overflow-hidden
                                    ${expandedCards[index] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                                `}>
                                    <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                                            <InputField
                                                label="Degree / Level"
                                                icon={<GraduationCap size={16} />}
                                                placeholder="e.g., Bachelor of Arts, Master of Science"
                                                value={edu.level}
                                                onChange={(v) => handleChangeField(index, "level", v)}
                                                error={localErrors[`level_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="School / University"
                                                icon={<School size={16} />}
                                                placeholder="e.g., Oxford University, Delhi University"
                                                value={edu.schoolName}
                                                onChange={(v) => handleChangeField(index, "schoolName", v)}
                                                error={localErrors[`schoolName_${index}`]}
                                                required
                                            />
                                            <InputField
                                                label="City"
                                                icon={<MapPin size={16} />}
                                                placeholder="e.g., London, New York"
                                                value={edu.city}
                                                onChange={(v) => handleChangeField(index, "city", v)}
                                                error={localErrors[`city_${index}`]}
                                                required
                                            />

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Globe size={16} /> Country <span className="text-rose-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <select
                                                        value={edu.country}
                                                        onChange={(e) => handleChangeField(index, "country", e.target.value)}
                                                        className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all appearance-none cursor-pointer text-sm
                                                            ${localErrors[`country_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                    >
                                                        <option value="">Select Country</option>
                                                        {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                </div>
                                                {localErrors[`country_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`country_${index}`]}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <InputField
                                                    label="Full Address"
                                                    icon={<MapPin size={16} />}
                                                    placeholder="Street address, building name, postal code"
                                                    value={edu.address}
                                                    onChange={(v) => handleChangeField(index, "address", v)}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Calendar size={16} /> Start Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={edu.from || ""}
                                                    onChange={(v) => handleChangeField(index, "from", v.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`from_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`from_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`from_${index}`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
                                                    <Calendar size={16} /> End Date <span className="text-rose-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={edu.to || ""}
                                                    onChange={(v) => handleChangeField(index, "to", v.target.value)}
                                                    min={edu.from}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                                        ${localErrors[`to_${index}`] ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                                />
                                                {localErrors[`to_${index}`] && (
                                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors[`to_${index}`]}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                        <button
                            type="button"
                            onClick={addEducation}
                            className="flex-1 flex items-center justify-center gap-2 bg-white border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 py-3 rounded-xl font-semibold text-sm transition-all"
                        >
                            <Plus size={18} /> Add Another Education Record
                        </button>

                    </div>
                </form>
            </div>
        </div>
    );
};

/* UI HELPERS */
const InputField = ({ label, value, onChange, type = "text", placeholder, icon, min, max, error, required }) => (
    <div>
        <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5 ml-1 flex items-center gap-2">
            {icon} {label} {required && <span className="text-rose-500">*</span>}
        </label>
        <input
            type={type}
            value={value || ""}
            min={min}
            max={max}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                ${error ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
        />
        {error && <p className="text-rose-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const Preview = ({ label, value }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[9px] sm:text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</span>
        <span className="text-gray-800 font-semibold text-sm break-words">{value || "—"}</span>
    </div>
);

export default EducationForm;